import { useState } from 'react';

import { FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { IntegrationTypes } from '../../../../../types/integrationTypes';

const LTI13SettingsDiv = styled.div`
    margin-top: 24px;
    display: flex;
    flex-direction: column;
`;

const StyledTextInputRow = styled.div`
    margin-top: 24px;
    display: flex;
`;

const StyledTextInputDiv = styled.div`
    width: 400px;
    margin-right: 24px;
    display: flex;
    flex-direction: column;
`;

const StyledHr = styled.hr`
    width: 100%;
    border: 1px solid ${(props) => props.theme.colors.border};
`;

interface LTI13TextFieldProps {
    label: string;
    name: string;
    value: string;
    error: boolean;
    helperText: string;
    handleChange: (event: any) => void;
}

const LTI13TextField: React.FC<LTI13TextFieldProps> = ({ label, name, value, error, helperText, handleChange }) => (
    <StyledTextInputDiv>
        <FormLabel>{label}</FormLabel>
        <TextField name={name} value={value} size='small' error={error} helperText={helperText} onChange={handleChange}/>
    </StyledTextInputDiv>
);

interface LTI13SettingsProps {
    consumerAppKey: string;
    consumerSecret: string;
    consumerClientId: string;
    consumerAuthorizationUrl: string;
    consumerAccessTokenUrl: string;
    consumerAuthorizationConfigUrl: string;
    integrationType: string;
    selectedLMS: string;
    handleChange: (event: any) => void;
}

const LTI13Settings: React.FC<LTI13SettingsProps> = ({
    consumerAppKey,
    consumerSecret,
    consumerClientId,
    consumerAuthorizationUrl,
    consumerAccessTokenUrl,
    consumerAuthorizationConfigUrl,
    integrationType,
    selectedLMS,
    handleChange
}) => {
    const { t } = useTranslation('admin_settings');
    const [clientIdError, setClientIdError] = useState<boolean>(false);
    const [authUrlError, setAuthUrlError] = useState<boolean>(false);
    const [accessTokenUrlError, setAccessTokenUrlError] = useState<boolean>(false);
    const [authConfigUrlError, setAuthConfigUrlError] = useState<boolean>(false);
    const [appKeyError, setAppKeyError] = useState<boolean>(false);
    const [secretError, setSecretError] = useState<boolean>(false);

    const setError = (handleError, event) => {
        const { value } = event.target;
        if (!value.length) {
            handleError(true);
        } else {
            handleError(false);
        }
        handleChange(event);
    };

    const selectedLmsLowercase = selectedLMS.toLowerCase();

    return (
        <LTI13SettingsDiv>
            <StyledHr/>
            <FormHelperText>{t('lti-13-helper-text')}</FormHelperText>
            <FormControl>
                {integrationType === IntegrationTypes.Blackboard &&
                    <StyledTextInputRow>
                        <LTI13TextField
                            label={t('learn-app-key')}
                            name='learn_app_key'
                            value={consumerAppKey}
                            error={appKeyError}
                            helperText={t('learn-app-key-helper')}
                            handleChange={(event) => setError(setAppKeyError, event)}
                        />
                        <LTI13TextField
                            label={t('learn-secret')}
                            name='learn_app_secret'
                            value={consumerSecret}
                            error={secretError}
                            helperText={t('learn-secret-helper')}
                            handleChange={(event) => setError(setSecretError, event)}
                        />
                    </StyledTextInputRow>
                }
                <StyledTextInputRow>
                    <LTI13TextField
                        label={t('lms-client-id')}
                        name='consumer_client_id'
                        value={consumerClientId}
                        error={clientIdError}
                        helperText={t('client-id-helper')}
                        handleChange={(event) => setError(setClientIdError, event)}
                    />
                    <LTI13TextField
                        label={t(`lms-authorization-url.${selectedLmsLowercase}`)}
                        name='consumer_authorization_url'
                        value={consumerAuthorizationUrl}
                        error={authUrlError}
                        helperText={t('auth-url-helper')}
                        handleChange={(event) => setError(setAuthUrlError, event)}
                    />
                </StyledTextInputRow>
                <StyledTextInputRow>
                    <LTI13TextField
                        label={t(`lms-access-token-url.${selectedLmsLowercase}`)}
                        name='consumer_access_token_url'
                        value={consumerAccessTokenUrl}
                        error={accessTokenUrlError}
                        helperText={t('access-token-helper')}
                        handleChange={(event) => setError(setAccessTokenUrlError, event)}
                    />
                    <LTI13TextField
                        label={t(`lms-authorization-config-url.${selectedLmsLowercase}`)}
                        name='consumer_authorization_config_url'
                        value={consumerAuthorizationConfigUrl}
                        error={authConfigUrlError}
                        helperText={t('auth-config-helper')}
                        handleChange={(event) => setError(setAuthConfigUrlError, event)}
                    />
                </StyledTextInputRow>
            </FormControl>
        </LTI13SettingsDiv>
    );
};

export default LTI13Settings;
